/**
 * AIThinkingDrawer — slide-out panel showing AI reasoning, decisions, signals.
 * Polls /ai-decisions + listens to socket.io 'ai:decision' events.
 */
import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { io as socketIo, type Socket } from "socket.io-client";

interface Decision {
  _id: string;
  symbol?: string;
  action: string;
  confidence: number;
  reasoning: string;
  steps?: { label: string; detail: string; timestamp: string }[];
  model: string;
  source: string;
  tags?: string[];
  createdAt: string;
}

interface Stats {
  last7Days: number;
  actionBreakdown: { _id: string; count: number }[];
  avgConfidence: { avg: number; min: number; max: number };
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const ACTION_COLORS: Record<string, string> = {
  BUY: "#6c5ce7",
  SELL: "#ff5252",
  HOLD: "#fdcb6e",
  SKIP: "#636e72",
  ANALYSIS: "#00cec9",
};

export default function AIThinkingDrawer({ open, onClose }: Props) {
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Fetch initial decisions
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get<{ count: number; decisions: Decision[] }>("/ai-decisions?limit=50"),
      api.get<Stats>("/ai-decisions/stats"),
    ]).then(([decRes, statsRes]) => {
      setDecisions(decRes.decisions || []);
      setStats(statsRes);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [open]);

  // Socket.io listener for real-time decisions
  useEffect(() => {
    if (!open) return;
    const socket = socketIo(window.location.origin, { path: "/socket.io" });
    socketRef.current = socket;

    socket.on("ai:decision", (decision: Decision) => {
      setDecisions((prev) => [decision, ...prev].slice(0, 100));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [open]);

  // Key handler to close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          zIndex: 9998, backdropFilter: "blur(2px)",
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 420, maxWidth: "90vw", background: "#1a1a2e",
        borderLeft: "1px solid #3a3a4e", zIndex: 9999,
        display: "flex", flexDirection: "column",
        boxShadow: "-4px 0 20px rgba(0,0,0,0.4)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #3a3a4e",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <h3 style={{ margin: 0, color: "#e0e0e0", fontSize: 16 }}>AI Thinking</h3>
            <span style={{ fontSize: 12, color: "#666" }}>
              {stats ? `${stats.last7Days} decisions (7d)` : "Loading..."}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "#666",
            fontSize: 20, cursor: "pointer", padding: 4,
          }}>
            x
          </button>
        </div>

        {/* Stats */}
        {stats && stats.actionBreakdown.length > 0 && (
          <div style={{
            padding: "10px 20px", borderBottom: "1px solid #2a2a3e",
            display: "flex", gap: 12, flexWrap: "wrap",
          }}>
            {stats.actionBreakdown.map((b) => (
              <span key={b._id} style={{ fontSize: 12 }}>
                <span style={{
                  color: ACTION_COLORS[b._id] || "#999",
                  fontWeight: 600,
                }}>
                  {b._id}
                </span>{" "}
                {b.count}
              </span>
            ))}
            <span style={{ fontSize: 12, color: "#666" }}>
              Avg confidence: {stats.avgConfidence.avg?.toFixed(0)}%
            </span>
          </div>
        )}

        {/* Decisions list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {loading && (
            <div style={{ color: "#666", textAlign: "center", padding: 20 }}>
              Loading decisions...
            </div>
          )}

          {!loading && decisions.length === 0 && (
            <div style={{ color: "#666", textAlign: "center", padding: 30 }}>
              <p style={{ fontSize: 14, margin: "0 0 8px" }}>No AI decisions yet</p>
              <p style={{ fontSize: 12, margin: 0 }}>
                Decisions will appear here when the scheduler runs or you place trades.
              </p>
            </div>
          )}

          {decisions.map((d) => (
            <div key={d._id} style={{
              padding: "12px 20px", borderBottom: "1px solid #22223a",
            }}>
              {/* Decision header */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: 6,
              }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{
                    background: ACTION_COLORS[d.action] || "#636e72",
                    color: "#fff", fontSize: 11, fontWeight: 600,
                    padding: "2px 8px", borderRadius: 4,
                  }}>
                    {d.action}
                  </span>
                  {d.symbol && (
                    <span style={{ color: "#e0e0e0", fontSize: 13, fontWeight: 500 }}>
                      {d.symbol}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: "#666" }}>
                  {new Date(d.createdAt).toLocaleString()}
                </span>
              </div>

              {/* Confidence bar */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{
                  flex: 1, height: 4, background: "#2a2a3e", borderRadius: 2,
                }}>
                  <div style={{
                    width: `${d.confidence}%`, height: "100%", borderRadius: 2,
                    background: d.confidence >= 70 ? "#6c5ce7" : d.confidence >= 40 ? "#fdcb6e" : "#ff5252",
                  }} />
                </div>
                <span style={{ fontSize: 11, color: "#888", minWidth: 30 }}>
                  {d.confidence}%
                </span>
              </div>

              {/* Reasoning */}
              <p style={{ margin: 0, fontSize: 12, color: "#a0a0b0", lineHeight: 1.5 }}>
                {d.reasoning}
              </p>

              {/* Steps */}
              {d.steps && d.steps.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {d.steps.map((s, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#666", paddingLeft: 8 }}>
                      <span style={{ color: "#00cec9" }}>-</span> {s.label}
                      {s.detail && <span>: {s.detail}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Tags + meta */}
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, color: "#555" }}>{d.model}</span>
                <span style={{ fontSize: 10, color: "#555" }}>{d.source}</span>
                {d.tags?.map((t) => (
                  <span key={t} style={{
                    fontSize: 10, background: "#2a2a3e", color: "#888",
                    padding: "1px 6px", borderRadius: 3,
                  }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
